import { Star, BadgeCheck, ExternalLink } from "lucide-react";
import { Reveal } from "./Reveal";
import { site } from "../config/site";
import { placeholderReviewText } from "../data/reviews";

export const Stars = ({ value = 5, className = "h-4 w-4" }) => {
  const full = Math.round(value);
  return (
    <span className="inline-flex items-center gap-1" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${className} ${i <= full ? "fill-champagne text-champagne" : "text-line"}`}
        />
      ))}
    </span>
  );
};

export const RatingSummary = ({ dark = false, large = false }) => (
  <div className={dark ? "text-ivory" : "text-ink"} data-testid="rating-summary">
    <div className="flex items-end gap-4">
      <span
        className={`font-display font-medium leading-none ${
          large ? "text-7xl md:text-8xl" : "text-6xl"
        }`}
      >
        {site.rating.value}
      </span>
      <div className="pb-1">
        <Stars value={site.rating.value} className={large ? "h-5 w-5" : "h-4 w-4"} />
        <p className={`mt-2 text-xs uppercase tracking-[0.18em] ${dark ? "text-ivory/60" : "text-soften"}`}>
          Google Rating · {site.rating.count} reviews
        </p>
      </div>
    </div>
  </div>
);

export const ReviewCard = ({ review, index = 0 }) => (
  <Reveal delay={index * 0.1} className="h-full">
    <figure
      className="flex h-full flex-col border border-dashed border-line bg-surface p-7 md:p-8"
      data-testid={`review-card-${review.id}`}
    >
      {review.placeholder ? (
        <>
          <span className="inline-flex w-fit items-center gap-1.5 border border-champagne/40 bg-champagne/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-taupe">
            <BadgeCheck className="h-3.5 w-3.5 text-champagne" />
            Placeholder
          </span>
          <blockquote className="mt-5 flex-1 font-display text-xl italic leading-relaxed text-soften md:text-2xl">
            “{placeholderReviewText}”
          </blockquote>
          <figcaption className="mt-6 border-t border-line pt-4 text-xs uppercase tracking-[0.16em] text-soften">
            Google Review
          </figcaption>
        </>
      ) : (
        <>
          <Stars value={review.rating} />
          <blockquote className="mt-5 flex-1 font-display text-xl leading-relaxed text-ink md:text-2xl">
            “{review.text}”
          </blockquote>
          <figcaption className="mt-6 border-t border-line pt-4 text-xs uppercase tracking-[0.16em] text-soften">
            {review.author} · Google Review
          </figcaption>
        </>
      )}
    </figure>
  </Reveal>
);

export const GoogleReviewsCTA = ({ className = "" }) => (
  <a
    href={site.googleReviewsUrl}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="google-reviews-cta"
    className={`group inline-flex min-h-[48px] items-center gap-2.5 border border-ink px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white md:text-sm ${className}`}
  >
    Read Our Google Reviews
    <ExternalLink className="h-4 w-4 text-champagne transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </a>
);
